// @flow
import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import NaurService from '../../services/naurService';
import useAbortController from '../useAbortController';

/**
 * Runs the property's first applicable fraction calculation. One-time and irreversible.
 *
 * Refreshing is invalidation only — react-query refetches, and the refetch covers the
 * fields the success payload omits (non-compliant unit counts, the NAUR event rows).
 * The refresh also runs on every error: an already-initialized property (someone else's
 * run) surfaces only through this refetch, and there is no status left to gate it on.
 */
const useInitializeApplicableFractionsMutation = ({
  organizationId,
  propertyId,
  options = {},
}: {
  organizationId: ?string,
  propertyId: ?string,
  options?: Object,
}) => {
  const naurService = useMemo(() => new NaurService(), []);
  const abortControllerOptions = useAbortController();
  const queryClient = useQueryClient();

  const refreshPropertyValues = useCallback(() => {
    queryClient.invalidateQueries([
      'naur-property-compliance-details',
      propertyId,
    ]);
    queryClient.invalidateQueries(['naurEvents']);
    queryClient.invalidateQueries([
      'applicable-fraction-initialization-state',
      propertyId,
    ]);
    queryClient.invalidateQueries(['property-naur-compliance', propertyId]);
  }, [queryClient, propertyId]);

  return useMutation(
    () =>
      naurService.initializeApplicableFractions(
        organizationId,
        propertyId,
        abortControllerOptions,
      ),
    {
      ...options,
      // Enforced, not defaulted: this POST is one-time and irreversible, so a caller
      // must not be able to turn retries back on.
      retry: false,
      // Composed, not overridable: a caller's own onSuccess/onError run in addition to
      // the cache invalidation below, never instead of it.
      onSuccess: (...args) => {
        refreshPropertyValues();
        options.onSuccess?.(...args);
      },
      onError: (error, ...rest) => {
        refreshPropertyValues();
        options.onError?.(error, ...rest);
      },
    },
  );
};

export default useInitializeApplicableFractionsMutation;
